import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import FAQSection from '../components/FAQSection';
import { faqs } from '../mockData';
import { Smartphone, Download, Zap, Shield, CheckCircle, Star, ChevronDown, ChevronUp } from 'lucide-react';

const AppPage = () => {
  const [isContentExpanded, setIsContentExpanded] = useState(false);

  useEffect(() => {
    document.title = 'Robocat Casino App | Mobile Spielen auf iOS & Android';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Robocat Casino App ⚡ Optimiert für iOS & Android ✓ Alle Spiele mobil verfügbar ✓ Schnelle mobile Auszahlungen ✓ Jetzt spielen');
    }
  }, []);

  const mobileFeatures = [
    {
      icon: <Zap className="w-8 h-8" />,
      title: 'Blitzschnelle Performance',
      description: 'Optimierte mobile Website mit schnellen Ladezeiten und flüssiger Navigation für ein erstklassiges Spielerlebnis.'
    },
    {
      icon: <Smartphone className="w-8 h-8" />,
      title: 'Responsive Design',
      description: 'Perfekt angepasst für alle Bildschirmgrößen - vom kleinen Smartphone bis zum großen Tablet. Automatische Anpassung garantiert.'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Sichere Transaktionen',
      description: 'Alle Ein- und Auszahlungen sind mobil möglich mit derselben Sicherheit wie auf dem Desktop durch SSL-Verschlüsselung.'
    },
    {
      icon: <Star className="w-8 h-8" />,
      title: 'Über 3.000 mobile Spiele',
      description: 'Zugriff auf das komplette Spielportfolio - Slots, Live-Casino und Tischspiele sind vollständig für mobile Geräte optimiert.'
    }
  ];

  const howToSteps = [
    {
      title: 'iOS (iPhone/iPad)',
      steps: [
        'Öffnen Sie Safari und besuchen Sie die Robocat Casino Website',
        'Tippen Sie auf das Teilen-Symbol (Quadrat mit Pfeil)',
        'Wählen Sie "Zum Home-Bildschirm" aus dem Menü',
        'Bestätigen Sie mit "Hinzufügen" - fertig!'
      ]
    },
    {
      title: 'Android (Smartphone/Tablet)',
      steps: [
        'Öffnen Sie Chrome und besuchen Sie die Robocat Casino Website',
        'Tippen Sie auf die drei Punkte (Menü) oben rechts',
        'Wählen Sie "Zum Startbildschirm hinzufügen"',
        'Bestätigen Sie mit "Hinzufügen" - fertig!'
      ]
    }
  ];

  const advantages = [
    'Keine App-Download erforderlich - sparen Sie Speicherplatz',
    'Immer die neueste Version - automatische Updates',
    'Funktioniert auf allen Geräten ohne Installation',
    'Sofortiger Zugriff über jeden mobilen Browser',
    'Identische Features wie Desktop-Version',
    'Touch-optimierte Bedienung für einfache Navigation'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 to-slate-900">
      {/* Header with Banner */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_futuristic-casino-1/artifacts/ovcbdn54_%D0%A1%D0%BD%D0%B8%D0%BC%D0%BE%D0%BA%20%D1%8D%D0%BA%D1%80%D0%B0%D0%BD%D0%B0%202025-11-09%20%D0%B2%2007.14.38.png" 
            alt="Robocat Casino App Banner" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950/95 via-slate-900/90 to-blue-950/95"></div>
        </div>
        
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-cyan-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-600 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Smartphone className="w-16 h-16 text-cyan-400 mx-auto mb-6" />
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Robocat Casino <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">App</span> - Mobiles Casino
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Spielen Sie Ihre Lieblingsspiele jederzeit und überall mit der optimierten mobilen Version von Robocat Casino. Keine App-Download erforderlich - greifen Sie direkt über Ihren Browser auf über 3.000 Spiele zu.
            </p>
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold px-8 py-6 text-lg shadow-2xl shadow-cyan-500/50 hover:shadow-cyan-500/70 transition-all duration-300"
              onClick={() => alert('Demo: Mobile Website öffnen')}
            >
              Jetzt mobil spielen
            </Button>
          </div>
        </div>
      </section>

      {/* Mobile Features */}
      <section className="py-16 bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-12 text-center">Vorteile der mobilen Robocat Casino Lösung</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {mobileFeatures.map((feature, index) => (
                <Card key={index} className="bg-slate-800/50 border-cyan-500/20 p-6 hover:shadow-xl hover:shadow-cyan-500/20 transition-all duration-300">
                  <div className="text-cyan-400 mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                  <p className="text-gray-300 leading-relaxed">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How to Add to Homescreen */}
      <section className="py-16 bg-slate-900">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <Download className="w-12 h-12 text-cyan-400 mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-white mb-4">So fügen Sie Robocat Casino zum Homescreen hinzu</h2>
              <p className="text-gray-400">Schneller Zugriff wie bei einer nativen App - in nur 3 Schritten</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {howToSteps.map((guide, index) => {
                const cardImages = [
                  "https://customer-assets.emergentagent.com/job_futuristic-casino-1/artifacts/kq6izy42_%D0%A1%D0%BD%D0%B8%D0%BC%D0%BE%D0%BA%20%D1%8D%D0%BA%D1%80%D0%B0%D0%BD%D0%B0%202025-11-09%20%D0%B2%2007.14.31.png",
                  "https://customer-assets.emergentagent.com/job_futuristic-casino-1/artifacts/tw7y7k87_%D0%A1%D0%BD%D0%B8%D0%BC%D0%BE%D0%BA%20%D1%8D%D0%BA%D1%80%D0%B0%D0%BD%D0%B0%202025-11-09%20%D0%B2%2007.14.15.png"
                ];
                
                return (
                  <Card key={index} className="bg-slate-800/50 border-cyan-500/20 p-8 relative overflow-hidden">
                    <div className="absolute inset-0 opacity-15">
                      <img 
                        src={cardImages[index]} 
                        alt={`${guide.title} Anleitung`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="relative z-10">
                      <h3 className="text-2xl font-bold text-white mb-6 text-center">{guide.title}</h3>
                      <div className="space-y-4">
                        {guide.steps.map((step, stepIndex) => (
                          <div key={stepIndex} className="flex items-start space-x-3">
                            <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                              {stepIndex + 1}
                            </div>
                            <p className="text-gray-300 pt-1">{step}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Advantages List */}
      <section className="py-16 bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-12 text-center">Warum keine native App?</h2>
            
            <Card className="bg-slate-800/50 border-cyan-500/20 p-8">
              <p className="text-gray-300 mb-6 leading-relaxed">
                Robocat Casino hat sich bewusst für eine Progressive Web App statt einer nativen App entschieden. Diese Entscheidung bietet Ihnen zahlreiche Vorteile:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {advantages.map((advantage, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">{advantage}</span>
                  </div>
                ))}
              </div>
              
              <p className="text-gray-300 mt-6 leading-relaxed">
                Die mobile Website bietet dieselbe Funktionalität wie eine native App, aber ohne die Nachteile. Sie müssen keine App-Store-Genehmigungen abwarten, keine großen Dateien herunterladen und keine regelmäßigen Updates durchführen. Die Website ist immer auf dem neuesten Stand!
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Mobile Payment Info */}
      <section className="py-16 bg-slate-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Mobile Ein- und Auszahlungen</h2>
            <Card className="bg-slate-800/50 border-cyan-500/20 p-8">
              <Shield className="w-12 h-12 text-cyan-400 mx-auto mb-4" />
              <p className="text-gray-300 leading-relaxed mb-6">
                Alle Zahlungsmethoden, die auf der Desktop-Version verfügbar sind, funktionieren auch mobil einwandfrei. Tätigen Sie sichere Ein- und Auszahlungen direkt von Ihrem Smartphone oder Tablet aus. PayPal, Kreditkarten, E-Wallets und Banküberweisungen werden unterstützt.
              </p>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-3xl font-bold text-cyan-400 mb-1">1-2h</div>
                  <div className="text-sm text-gray-400">E-Wallet Auszahlung</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-cyan-400 mb-1">256-Bit</div>
                  <div className="text-sm text-gray-400">SSL-Verschlüsselung</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-cyan-400 mb-1">100%</div>
                  <div className="text-sm text-gray-400">Sicher & Geschützt</div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* SEO Content with Dropdown - Before FAQs */}
      <section className="py-16 bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-slate-800/50 border border-cyan-500/20 rounded-2xl overflow-hidden">
              <button
                onClick={() => setIsContentExpanded(!isContentExpanded)}
                className="w-full px-8 py-6 flex items-center justify-between hover:bg-slate-800/70 transition-colors"
              >
                <h2 className="text-2xl md:text-3xl font-bold text-white text-left">
                  Robocat Casino App: Vollständiger mobiler Zugriff
                </h2>
                {isContentExpanded ? (
                  <ChevronUp className="w-6 h-6 text-cyan-400 flex-shrink-0 ml-4" />
                ) : (
                  <ChevronDown className="w-6 h-6 text-cyan-400 flex-shrink-0 ml-4" />
                )}
              </button>
              
              <div className="px-8 pb-8 space-y-6">
                <p className="text-gray-300 leading-relaxed">
                  Die Robocat Casino App bietet ein nahtloses mobiles Spielerlebnis ohne die Notwendigkeit eines separaten Downloads...
                </p>
                
                {isContentExpanded && (
                  <div className="space-y-6 animate-in slide-in-from-top duration-300">
                    <div className="relative">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_futuristic-casino-1/artifacts/kxxlhmeq_%D0%A1%D0%BD%D0%B8%D0%BC%D0%BE%D0%BA%20%D1%8D%D0%BA%D1%80%D0%B0%D0%BD%D0%B0%202025-11-09%20%D0%B2%2008.36.35.png" 
                        alt="Robocat Casino Mobile App" 
                        className="float-right ml-6 mb-4 w-2/5 rounded-lg border border-cyan-500/20 shadow-xl"
                      />
                      <p className="text-gray-300 leading-relaxed mb-4">
                        Die Robocat Casino App bietet ein nahtloses mobiles Spielerlebnis ohne die Notwendigkeit eines separaten Downloads. Anstelle einer nativen App für iOS oder Android setzt Robocat auf eine hochmoderne Progressive Web App (PWA)-Technologie. Diese Lösung bietet alle Vorteile einer nativen App - schnelle Ladezeiten, Offline-Funktionalität für bestimmte Bereiche und die Möglichkeit, die Website zum Homescreen hinzuzufügen - ohne den Nachteil von Speicherplatzbedarf oder komplizierten Installations- und Update-Prozessen.
                      </p>
                      
                      <p className="text-gray-300 leading-relaxed mb-4">
                        Das Robocat Casino APK ist nicht erforderlich, da die mobile Website über jeden modernen Browser zugänglich ist. Dies bedeutet, dass Android-Nutzer keine APK-Datei herunterladen und installieren müssen, was oft Sicherheitsbedenken aufwirft. Stattdessen können Sie einfach die Robocat Casino Website in Chrome, Firefox oder einem anderen Browser Ihrer Wahl öffnen und sofort mit dem Spielen beginnen. Die mobile Version ist vollständig responsive und passt sich automatisch an die Bildschirmgröße Ihres Geräts an - ob Smartphone oder Tablet.
                      </p>
                      <div className="clear-both"></div>
                    </div>
                    
                    <div className="relative">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_futuristic-casino-1/artifacts/gsfoxnds_%D0%A1%D0%BD%D0%B8%D0%BC%D0%BE%D0%BA%20%D1%8D%D0%BA%D1%80%D0%B0%D0%BD%D0%B0%202025-11-09%20%D0%B2%2008.36.50.png" 
                        alt="Robocat Casino Mobile Features" 
                        className="float-left mr-6 mb-4 w-2/5 rounded-lg border border-cyan-500/20 shadow-xl"
                      />
                      <p className="text-gray-300 leading-relaxed mb-4">
                        Die Robocat App bietet Zugriff auf das komplette Spielportfolio von über 3.000 Spielen. Mehr als 95% aller Spiele sind vollständig für mobile Geräte optimiert, einschließlich der beliebtesten Slots von Pragmatic Play, Play'n GO und Hacksaw Gaming sowie aller Live-Casino-Spiele von Evolution Gaming. Die Touch-Bedienung ist intuitiv und reibungslos, mit großen, gut zugänglichen Schaltflächen und einer vereinfachten Navigation, die speziell für mobile Geräte entwickelt wurde. Funktionen wie Wischen, Tippen und Ziehen sind perfekt in das Spielerlebnis integriert.
                      </p>
                      
                      <p className="text-gray-300 leading-relaxed">
                        Ein besonderer Vorteil der mobilen Robocat Casino Lösung ist die Möglichkeit, das Icon zum Homescreen hinzuzufügen. Sowohl auf iOS als auch auf Android können Sie die Website wie eine native App zum Startbildschirm hinzufügen. Dies ermöglicht einen Ein-Tap-Zugriff auf Robocat Casino, ohne dass Sie jedes Mal den Browser öffnen und die URL eingeben müssen. Die Website startet dann im Vollbildmodus und fühlt sich genau wie eine native App an. Alle Funktionen der Desktop-Version sind verfügbar: Einzahlungen, Auszahlungen, Kontoverwaltung, Bonusaktivierung und natürlich das komplette Spielangebot.
                      </p>
                      <div className="clear-both"></div>
                    </div>
                  </div>
                )}
                
                <button
                  onClick={() => setIsContentExpanded(!isContentExpanded)}
                  className="text-cyan-400 hover:text-cyan-300 font-semibold transition-colors flex items-center space-x-2"
                >
                  <span>{isContentExpanded ? 'Weniger anzeigen' : 'Mehr anzeigen'}</span>
                  {isContentExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection faqs={faqs.app} title="Häufige Fragen zur Robocat Casino App" />
    </div>
  );
};

export default AppPage;
